import { ElectronicSignatureDocumentType, ElectronicSignatureProvider } from "@/api-schema-enums";
import type { CompanyGroupSearchResponse } from "@/common/api/company-group/use-company-group-search.query";
import type {
  ElectronicSignatureTemplateConfigurationSearchResponse,
  ElectronicSignatureTemplateConfigurationUpsertBody,
} from "@/common/api/electronic-signature-template-configuration/electronic-signature-template-configuration.types";
import { normalizePositiveSafeIntegerIdentifier } from "@/common/helpers/normalize-numeric-identifier";

type CompanyGroup = CompanyGroupSearchResponse["data"][number];
type TemplateConfiguration = ElectronicSignatureTemplateConfigurationSearchResponse["data"][number];

export interface ElectronicSignatureTemplateConfigurationRow {
  companyGroup: CompanyGroup;
  configuration: TemplateConfiguration;
  documentType: ElectronicSignatureDocumentType;
  draftKey: string;
  isDirty: boolean;
  isValid: boolean;
  normalizedValue: string | undefined;
  provider: ElectronicSignatureProvider;
  storedValue: string;
  value: string;
}

export type ElectronicSignatureTemplateConfigurationDrafts = Readonly<Record<string, string>>;

export interface ElectronicSignatureTemplateConfigurationCreateDraft {
  companyGroupUuid: string;
  templateId: string;
}

export type ElectronicSignatureTemplateConfigurationCreateDrafts = Readonly<
  Record<string, ElectronicSignatureTemplateConfigurationCreateDraft>
>;

export const EMPTY_ELECTRONIC_SIGNATURE_TEMPLATE_CONFIGURATION_CREATE_DRAFT: ElectronicSignatureTemplateConfigurationCreateDraft =
  {
    companyGroupUuid: "",
    templateId: "",
  };

export const ELECTRONIC_SIGNATURE_TEMPLATE_ID_INPUT_PATTERN = "[0-9]*";

export function getElectronicSignatureTemplateConfigurationTypeKey(
  provider: ElectronicSignatureProvider,
  documentType: ElectronicSignatureDocumentType,
): string {
  return `${provider}:${documentType}`;
}

export function getElectronicSignatureTemplateConfigurationKey(
  provider: ElectronicSignatureProvider,
  documentType: ElectronicSignatureDocumentType,
  companyGroupUuid: string,
): string {
  return `${provider}:${documentType}:${companyGroupUuid}`;
}

export function normalizeElectronicSignatureTemplateId(
  provider: ElectronicSignatureProvider,
  templateId: string,
): string | undefined {
  if (provider === ElectronicSignatureProvider.Paperless) {
    return normalizePositiveSafeIntegerIdentifier(templateId);
  }

  const normalizedTemplateId = templateId.trim();
  return normalizedTemplateId || undefined;
}

export function sanitizeElectronicSignatureTemplateIdInput(
  provider: ElectronicSignatureProvider,
  value: string,
): string {
  return provider === ElectronicSignatureProvider.Paperless ? value.replace(/\D/g, "") : value;
}

export function setElectronicSignatureTemplateConfigurationDraft(
  drafts: ElectronicSignatureTemplateConfigurationDrafts,
  key: string,
  value: string,
): ElectronicSignatureTemplateConfigurationDrafts {
  return { ...drafts, [key]: value };
}

export function clearElectronicSignatureTemplateConfigurationDraft(
  drafts: ElectronicSignatureTemplateConfigurationDrafts,
  key: string,
): ElectronicSignatureTemplateConfigurationDrafts {
  const nextDrafts = { ...drafts };
  delete nextDrafts[key];
  return nextDrafts;
}

export function setElectronicSignatureTemplateConfigurationCreateDraft(
  drafts: ElectronicSignatureTemplateConfigurationCreateDrafts,
  key: string,
  draft: ElectronicSignatureTemplateConfigurationCreateDraft,
): ElectronicSignatureTemplateConfigurationCreateDrafts {
  return { ...drafts, [key]: draft };
}

export function clearElectronicSignatureTemplateConfigurationCreateDraft(
  drafts: ElectronicSignatureTemplateConfigurationCreateDrafts,
  key: string,
): ElectronicSignatureTemplateConfigurationCreateDrafts {
  const nextDrafts = { ...drafts };
  delete nextDrafts[key];
  return nextDrafts;
}

export function buildElectronicSignatureTemplateConfigurationRows(
  configurations: TemplateConfiguration[] | undefined,
  drafts: ElectronicSignatureTemplateConfigurationDrafts,
  provider: ElectronicSignatureProvider,
  documentType: ElectronicSignatureDocumentType,
  locale: string,
): ElectronicSignatureTemplateConfigurationRow[] {
  return (configurations ?? [])
    .filter((configuration) => configuration.provider === provider && configuration.documentType === documentType)
    .sort((left, right) => left.companyGroup.name.localeCompare(right.companyGroup.name, locale))
    .map((configuration) => {
      const companyGroup = configuration.companyGroup;
      const draftKey = getElectronicSignatureTemplateConfigurationKey(provider, documentType, companyGroup.uuid);
      const storedValue = configuration.templateId;
      const value = drafts[draftKey] ?? storedValue;
      const normalizedValue = normalizeElectronicSignatureTemplateId(provider, value);

      return {
        companyGroup,
        configuration,
        documentType,
        draftKey,
        isDirty: drafts[draftKey] !== undefined && value.trim() !== storedValue,
        isValid: normalizedValue !== undefined,
        normalizedValue,
        provider,
        storedValue,
        value,
      };
    });
}

export function buildRemainingCompanyGroupOptions(
  companyGroups: CompanyGroup[] | undefined,
  configurations: TemplateConfiguration[] | undefined,
  provider: ElectronicSignatureProvider,
  documentType: ElectronicSignatureDocumentType,
  locale: string,
): { label: string; value: string }[] {
  const configuredCompanyGroupUuids = new Set(
    (configurations ?? [])
      .filter((configuration) => configuration.provider === provider && configuration.documentType === documentType)
      .map((configuration) => configuration.companyGroup.uuid),
  );

  return (companyGroups ?? [])
    .filter((companyGroup) => !configuredCompanyGroupUuids.has(companyGroup.uuid))
    .sort((left, right) => left.name.localeCompare(right.name, locale))
    .map((companyGroup) => ({ label: companyGroup.name, value: companyGroup.uuid }));
}

export function buildElectronicSignatureTemplateConfigurationUpsertBody(
  row: ElectronicSignatureTemplateConfigurationRow,
): ElectronicSignatureTemplateConfigurationUpsertBody | undefined {
  if (!row.isDirty || row.normalizedValue === undefined) {
    return undefined;
  }

  return {
    companyGroupUuid: row.companyGroup.uuid,
    provider: row.provider,
    documentType: row.documentType,
    templateId: row.normalizedValue,
  };
}

export function buildElectronicSignatureTemplateConfigurationCreateBody(
  draft: ElectronicSignatureTemplateConfigurationCreateDraft,
  provider: ElectronicSignatureProvider,
  documentType: ElectronicSignatureDocumentType,
): ElectronicSignatureTemplateConfigurationUpsertBody | undefined {
  const companyGroupUuid = draft.companyGroupUuid.trim();
  const templateId = normalizeElectronicSignatureTemplateId(provider, draft.templateId);

  if (!companyGroupUuid || !templateId) {
    return undefined;
  }

  return {
    companyGroupUuid,
    provider,
    documentType,
    templateId,
  };
}

export function buildElectronicSignatureTemplateConfigurationDeleteInput(
  row: ElectronicSignatureTemplateConfigurationRow,
): { uuid: string } {
  return { uuid: row.configuration.uuid };
}
